// src/pages/AuctionDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../utils/firebaseconfig";
import "../styles/productDetails.css";
import { checkVerification } from "../utils/verificationCheck";

function AuctionDetails() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidders, setBidders] = useState([]);
  const [usernames, setUsernames] = useState({});
  const [timeLeft, setTimeLeft] = useState("");
  const [auctionEnded, setAuctionEnded] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        setIsVerified(user.emailVerified);
      }
    });
  }, []);

  useEffect(() => {
    const fetchAuction = async () => {
      try {
        const auctionDoc = await getDoc(doc(db, "Auction", auctionId));
        if (auctionDoc.exists()) {
          const auctionData = auctionDoc.data();
          setAuction({ id: auctionDoc.id, ...auctionData });
          setBidders(auctionData.bidders || []);
          fetchUsernames(auctionData.bidders || []);
        }
      } catch (err) {
        console.error("Error fetching auction:", err);
      }
    };
    fetchAuction();
  }, [auctionId]);

  useEffect(() => {
    if (auction?.endDate) {
      const interval = setInterval(() => {
        const now = new Date();
        const end = new Date(auction.endDate);
        const diff = end - now;
        if (diff <= 0) {
          setTimeLeft("Auction ended");
          setAuctionEnded(true);
          clearInterval(interval);
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff / (1000 * 60)) % 60);
          const seconds = Math.floor((diff / 1000) % 60);
          setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [auction?.endDate]);

  const fetchUsernames = async (biddersList) => {
    const updatedUsernames = { ...usernames };
    for (const bid of biddersList) {
      if (bid.userId && !updatedUsernames[bid.userId]) {
        try {
          const userDoc = await getDoc(doc(db, "User", bid.userId));
          updatedUsernames[bid.userId] = userDoc.exists() ? userDoc.data().username || "Unknown" : "Unknown";
        } catch (err) {
          console.error("Error fetching username:", err);
        }
      }
    }
    setUsernames(updatedUsernames);
  };

  const handlePlaceBid = async () => {
    if (!currentUser) {
      alert("You must be logged in to place a bid.");
      return;
    }

    // Check if user is verified
    if (!isVerified) {
      if (!checkVerification(navigate)) {
        return;
      }
    }

    // ❌ Prevent auction owner from bidding
    if (auction.ownerID === currentUser.uid) {
      alert("You cannot bid on your own auction.");
      return;
    }

    if (!bidAmount || parseFloat(bidAmount) <= auction.currentBid) {
      alert("Bid amount must be higher than current bid.");
      return;
    }

    const newBid = {
      userId: currentUser.uid,
      amount: parseFloat(bidAmount),
      timestamp: new Date().toISOString()
    };

    const updatedBidders = [...(auction.bidders || []), newBid];

    try {
      await updateDoc(doc(db, "Auction", auction.id), {
        currentBid: newBid.amount,
        bidders: updatedBidders
      });

      setAuction((prev) => ({ ...prev, currentBid: newBid.amount, bidders: updatedBidders }));
      setBidders(updatedBidders);
      fetchUsernames(updatedBidders);
      setBidAmount("");
    } catch (error) {
      console.error("Error placing bid:", error);
      alert("Failed to place bid. Please try again later.");
    }
  };

  if (!auction) {
    return <h1 className="product-details">Auction not found</h1>;
  }

  return (
    <div className="product-details-container">
      <div className="product-top">
        <div className="product-gallery">
          <img
            src={auction.image || "https://via.placeholder.com/300"}
            alt={auction.title}
          />
        </div>
        <div className="product-info">
          <h1 className="product-title">{auction.title}</h1>
          <p className="product-price">Current Bid: AED {auction.currentBid}</p>
          <p className="auctioneer">Auctioneer: {auction.auctioneer}</p>
          <p className="auction-date">Start Date: {new Date(auction.startDate).toLocaleString()}</p>
          <p className="auction-date">End Date: {new Date(auction.endDate).toLocaleString()}</p>
          <p className="auction-time-left">Time Left: {timeLeft}</p>

          {currentUser && !auctionEnded && (
            <div className="bid-section">
              <h4>Place a Bid</h4>
              
              {!isVerified && (
                <div className="verification-warning bid-verification">
                  <p>Email verification required to place bids</p>
                  <button 
                    onClick={() => checkVerification(navigate)}
                    className="verify-email-button"
                  >
                    Verify Email
                  </button>
                </div>
              )}
              
              <input
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                placeholder="Enter bid amount in AED"
                disabled={!isVerified}
              />
              <button 
                className="bid-button" 
                onClick={handlePlaceBid}
                disabled={!isVerified}
              >
                {isVerified ? "Place a Bid" : "Verify Email to Bid"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="comments-section">
        <h3>Bidders</h3>
        {bidders.length > 0 ? (
          <ul className="comments-list">
            {bidders.map((bid, index) => (
              <li key={index}>
                <strong>{usernames[bid.userId] || "Unknown"}</strong>: AED {bid.amount} <em>({new Date(bid.timestamp).toLocaleString()})</em>
              </li>
            ))}
          </ul>
        ) : (
          <p className="no-comments">No bids yet.</p>
        )}
      </div>
    </div>
  );
}

export default AuctionDetails;
